import React from "react";
import { useTranslation } from "react-i18next";
import { FemaleFetus } from "../../../assets/iconComponents/FemaleFetus";
import { MaleFetus } from "../../../assets/iconComponents/MaleFetus";
import { STANDALONE_FETAL_SEX } from "../../../recoil/types";
import { GetTypographyContent } from "../Typography/Typography";
import {
  FetalSexResultBlockValue,
  isTwinStandaloneFetalSex,
} from "./fetalSexResultUtils";
import {
  FetalSexResultBlockWrapper,
  FetalSexResultGrid,
  FetalSexResultLeadIn,
  FetalSexResultRow,
} from "./FetalSexResultBlock.styles";

// Presentation-only fetal sex result block (no recoil, no router) shared by
// the logged-in standalone card and the logged-out receiver landing page.
interface FetalSexResultBlockProps {
  fetalSex: FetalSexResultBlockValue;
  leadInText: string;
}

const getFetalSexResultRows = (
  fetalSex: FetalSexResultBlockValue,
  labels: { boy: string; girl: string; oneOrBothBoys: string },
): React.ReactNode => {
  if (fetalSex === STANDALONE_FETAL_SEX.ONE_OR_BOTH_MALES) {
    return (
      <FetalSexResultRow $extend data-testid="fetal-sex-result-row">
        <MaleFetus />
        {GetTypographyContent({
          content: labels.oneOrBothBoys,
          size: "h2",
          textColor: "greyBody",
          isBold: true,
        })}
      </FetalSexResultRow>
    );
  }

  const renderResultRow = (sex: string, key: string): React.ReactNode => {
    const isBoy = sex.trim() === STANDALONE_FETAL_SEX.MALE;
    return (
      <FetalSexResultRow data-testid="fetal-sex-result-row" key={key}>
        {isBoy ? <MaleFetus /> : <FemaleFetus />}
        {GetTypographyContent({
          content: isBoy ? labels.boy : labels.girl,
          size: "h2",
          textColor: "greyBody",
          isBold: true,
        })}
      </FetalSexResultRow>
    );
  };
  const [firstFetus, secondFetus] = fetalSex.split("&");
  return (
    <>
      {renderResultRow(firstFetus, "first-fetus")}
      {secondFetus && renderResultRow(secondFetus, "second-fetus")}
    </>
  );
};

export const FetalSexResultBlock = ({
  fetalSex,
  leadInText,
}: FetalSexResultBlockProps): React.ReactElement => {
  const { t } = useTranslation();
  const isTwin = isTwinStandaloneFetalSex(fetalSex);

  return (
    <FetalSexResultBlockWrapper data-testid="fetal-sex-result-block">
      <FetalSexResultLeadIn>
        {GetTypographyContent({
          content: leadInText,
          size: "medium",
          textColor: "greyBody",
        })}
      </FetalSexResultLeadIn>
      <FetalSexResultGrid
        aria-live="polite"
        $isTwin={isTwin}
        data-testid="fetal-sex-result-value"
      >
        {getFetalSexResultRows(fetalSex, {
          boy: t("fetalSex.result.boy"),
          girl: t("fetalSex.result.girl"),
          oneOrBothBoys: t("fetalSex.result.oneOrBothBoys"),
        })}
      </FetalSexResultGrid>
    </FetalSexResultBlockWrapper>
  );
};
